import { z } from 'zod';
import { baseModelSchema } from '../../baseModel';
import { IProjectFormValues } from './projectFormValues';

/**
 * Zod schema for validating project data.
 * Extends the base model schema with project-specific fields.
 *
 * @property {string} [number] - Project number identifier (max 255 characters)
 * @property {string} [description] - Description of the project (max 255 characters)
 * @property {number} [commodityId] - ID of the commodity associated with this project
 * @property {boolean} [isImported] - Whether the project is imported
 * @property {Date} [endedOn] - Optional date when the project ended
 */
export const projectSchema = baseModelSchema.extend({
    number: z.string().nonempty('Number is required').max(255, 'Number can only be up to 255 characters long.').default(''),
    description: z.string().nonempty('Description is required').max(255, 'Description can only be up to 255 characters long.').default(''),
    commodityId: z.number(),
    isImported: z.boolean().default(false),
    endedOn: z.coerce.date().optional(),
});

/**
 * TypeScript interface representing a Project object.
 * Generated from the Zod schema for type safety.
 */
export type IProject = z.infer<typeof projectSchema>;

/**
 * Creates a validated Project object from form values.
 *
 * @param {IProjectFormValues} formValues - The form data to convert
 * @returns {IProject} A validated project object
 * @throws {ZodError} If validation fails
 */
export function Project(formValues : IProjectFormValues) : IProject {
    const { commodity } = formValues;
    return projectSchema.parse({
        ...formValues,
        commodityId: commodity?.value ? Number(commodity.value) : undefined,
    });
};
